<script setup>
import { computed } from 'vue';
import CheckCircleIcon from '@/assets/icons/check-circle.svg';
import StatusSuccessIcon from '@/assets/icons/status-success.svg';
import StatusWarningIcon from '@/assets/icons/status-warning.svg';
import StatusErrorIcon from '@/assets/icons/status-error.svg';
import CloseIcon from '@/assets/icons/close.svg';

/**
 * InToast - status(enabled/success/warning/error)별 아이콘·색상 토스트.
 * Figma KKwV3FylHy4M7KScqFhblf:2559:115977 (Feedback/Toast)
 */

const props = defineProps({
  status: {
    type: String,
    default: 'enabled',
    validator: (v) => ['enabled', 'success', 'warning', 'error'].includes(v),
  },
  message: { type: String, default: 'Message' },
  closable: { type: Boolean, default: false },
  // true 면 반투명 tint 대신 불투명 등가색으로. 기본 false = Figma 진본 유지.
  opaque: { type: Boolean, default: false },
});

defineEmits(['close']);   // close 는 template 의 $emit('close') 로 직접 발신

const statusIcon = computed(() => {
  switch (props.status) {
    case 'success': return StatusSuccessIcon;
    case 'warning': return StatusWarningIcon;
    case 'error':   return StatusErrorIcon;
    default:        return CheckCircleIcon;       // Enabled - brand check-circle
  }
});
</script>

<template>
  <div
    class="in-toast"
    :class="[`in-toast--${status}`, { 'in-toast--opaque': opaque }]"
    role="status"
    aria-live="polite"
  >
    <span class="in-toast__icon" aria-hidden="true">
      <slot name="icon">
        <img :src="statusIcon" alt="" />
      </slot>
    </span>
    <span class="in-toast__message">
      <slot>{{ message }}</slot>
    </span>
    <button
      v-if="closable"
      type="button"
      class="in-toast__close"
      aria-label="닫기"
      @click="$emit('close')"
    >
      <img :src="CloseIcon" alt="" />
    </button>
  </div>
</template>

<style scoped>
.in-toast {
  position: relative;
  isolation: isolate;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  width: 320px;
  background: var(--in-surface-white);
  border: 1px solid var(--in-border-brand);
  border-radius: var(--in-radius-sm);
  box-shadow: var(--in-shadow-elev-1);
  padding: 16px 14px 16px 20px;
  font-family: var(--in-font-family-body);
  font-size: var(--in-font-size-lg);
  line-height: var(--in-line-height-lg);
  font-weight: var(--in-font-weight-regular);
  letter-spacing: var(--in-letter-spacing-lg);
  color: var(--in-text-accent);
}

.in-toast::before {
  position: absolute;
  z-index: 0;
  inset: 0;
  border-radius: inherit;
  background: transparent;
  content: '';
  pointer-events: none;
}

.in-toast > * {
  position: relative;
  z-index: 1;
}

.in-toast__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}
.in-toast__icon img { width: 19px; height: 19px; display: block; }

/* 여러 줄 허용. 한 줄 말줄임이면 필수 누락 목록처럼 긴 메시지가 잘려 무엇이 빠졌는지 읽을 수 없다. */
.in-toast__message {
  line-height: 24px;
  flex: 1 1 0;
  min-width: 0;
  white-space: pre-line;
  overflow-wrap: anywhere;
}

.in-toast__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  border-radius: var(--in-radius-xxs);
  transition: background 120ms ease;
}
.in-toast__close:hover { background: var(--in-surface-state-default); }
.in-toast__close img { width: 100%; height: 100%; display: block; }

/* === Status - Figma 진본: border + tint bg (기본 = 반투명 tint) === */
.in-toast--enabled {
  background: var(--in-surface-white);                  /* enabled 는 원래 불투명 */
  border-color: var(--in-border-brand);
  color: var(--in-text-accent);
}
/* green 테마 - enabled 아이콘은 img 렌더라 fill 이 파랑으로 고정돼 색상 시프트로 맞춘다.
   success/warning/error 는 시맨틱 상태색이라 제외한다. */
:root[data-theme='green'] .in-toast--enabled .in-toast__icon img {
  filter: hue-rotate(-49deg) saturate(48%) brightness(85%) contrast(160%); /* #0d9bdb->#108351(목표 #117f53 거리5)·흰색/회색 보존 - canvas 최적화 2026-08-06 (구 체인은 구green #1aa769) */
}
.in-toast--success {
  background: var(--in-surface-white);
  border-color: var(--in-border-success);               /* #37ae34 */
  color: var(--in-text-success);
}
.in-toast--warning {
  background: var(--in-surface-white);
  border-color: var(--in-border-warning);               /* #ed6c02 */
  color: var(--in-text-warning);
}
.in-toast--error {
  background: var(--in-surface-white);
  border-color: var(--in-border-error);                 /* #e33131 */
  color: var(--in-text-error);
}

.in-toast--success::before { background: var(--in-surface-success); }
.in-toast--warning::before { background: var(--in-surface-warning); }
.in-toast--error::before { background: var(--in-surface-error); }

/* opaque 옵션 - tint 대신 불투명 등가 토큰(--in-surface-*-solid). */
/*   기본(반투명)은 Figma 진본 유지, prop opaque 시에만 배경을 불투명 파스텔로 전환. */
.in-toast--opaque.in-toast--success::before { background: var(--in-surface-success-solid); }
.in-toast--opaque.in-toast--warning::before { background: var(--in-surface-warning-solid); }
.in-toast--opaque.in-toast--error::before   { background: var(--in-surface-error-solid); }
/* enabled 는 원래 불투명(--in-surface-white)이라 opaque 추가 처리 불필요. */
</style>
